import { ReactNode, useMemo } from "react";

import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from "components/layout";
import {
  isStatusReleaseStage,
  PlatformPublication,
  RELEASE_STAGES,
  ReleaseStage,
  STATUS_LABELS,
  TiVoPublications,
} from "hooks/tivoApps";

import { TEXTS } from "../constants";
import styles from "./VariantPublicationTable.scss";

type OemPublicationRenderer = (props: {
  variantReleaseStages: ReleaseStage[];
  oemPublication: PlatformPublication["publications"][string];
  applicableReleaseStages: ReleaseStage[];
  platformId: string;
  oemId: string;
  clientTypeId?: string;
  releaseStage: ReleaseStage;
}) => ReactNode;

interface VariantPublicationTableProps {
  platformId: string;
  publication: TiVoPublications;
  withoutClientTypes?: boolean;

  children: OemPublicationRenderer;
}

export function VariantPublicationTable({
  platformId,
  publication,
  withoutClientTypes = false,
  children,
}: VariantPublicationTableProps) {
  const variantReleaseStages = useMemo(
    () => publication.variant?.status.filter(isStatusReleaseStage) || [],
    [publication]
  );

  const renderBody = () => {
    const pformPub = publication.platforms.find(
      ({ platformId: id }) => id === platformId
    );
    if (!pformPub) {
      return null;
    }
    return Object.entries(pformPub.publications).map(([oemId, oemPub]) => {
      const oem = publication.oems?.find(({ id }) => id === oemId) ?? {
        id: "unknown",
        slug: "unknown",
        name: TEXTS.PUBLICATIONS.UNKNOWN_OEM,
        applicableReleaseStages: [],
        platformId: "unknown",
        publishingConfirmationRequired: "unknown",
      };

      if (
        withoutClientTypes ||
        !oem.clientTypes ||
        oem.clientTypes.length === 0
      ) {
        return (
          <TableRow key={`${oemId}-all`}>
            <TableRowColumn>{oem.name}</TableRowColumn>
            <TableRowColumn>All client types</TableRowColumn>
            {RELEASE_STAGES.map((rs) => (
              <TableRowColumn key={rs}>
                {children({
                  variantReleaseStages,
                  oemPublication: oemPub,
                  applicableReleaseStages: oem.applicableReleaseStages,
                  platformId,
                  oemId,
                  releaseStage: rs,
                })}
              </TableRowColumn>
            ))}
            <TableRowColumn />
          </TableRow>
        );
      }

      return oem.clientTypes.map((ct, ctIdx) => {
        return (
          <TableRow key={`${oemId}-${ct.id}`}>
            {ctIdx === 0 && (
              <TableRowColumn rowspan={oem.clientTypes?.length}>
                {oem.name}
              </TableRowColumn>
            )}
            <TableRowColumn>{ct.name}</TableRowColumn>
            {RELEASE_STAGES.map((rs) => (
              <TableRowColumn key={rs}>
                {children({
                  variantReleaseStages,
                  oemPublication: oemPub,
                  applicableReleaseStages: oem.applicableReleaseStages,
                  platformId,
                  oemId,
                  clientTypeId: ct.id,
                  releaseStage: rs,
                })}
              </TableRowColumn>
            ))}
            <TableRowColumn />
          </TableRow>
        );
      });
    });
  };

  return (
    <Table>
      <TableHeader>
        <TableHeaderColumn className={styles.oemCol}>
          {TEXTS.PUBLICATIONS.OEM_COL}
        </TableHeaderColumn>
        <TableHeaderColumn className={styles.clientTypeCol}>
          {TEXTS.PUBLICATIONS.CLIENT_TYPE_COL}
        </TableHeaderColumn>
        {RELEASE_STAGES.map((releaseStage) => (
          <TableHeaderColumn
            key={releaseStage}
            className={styles.releaseStageCol}
          >
            {STATUS_LABELS[releaseStage]}
          </TableHeaderColumn>
        ))}
        <TableHeaderColumn />
      </TableHeader>
      <TableBody>{renderBody()}</TableBody>
    </Table>
  );
}
